import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import EmailIcon from "@mui/icons-material/Email";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { toast } from "react-toastify";
import {} from "../../utils/urlUtils";

import AppCircularProgress from "../common/AppCircularProgress";
import AppButton from "../buttons/AppButton";
import AppEmailModal from "../common/AppEmailModal";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  getCreditNotePdf,
  emailCreditNote,
} from "../../redux/actions/creditNoteActions";
import { creditNoteActions } from "../../redux/slices/creditNote";
import { CreditNoteResponseDto } from "../../shared/dtos/creditNote/CreditNoteResponseDto";

interface CreditNotePdfTabProps {
  note: CreditNoteResponseDto;
}

const CreditNotePdfTab: React.FC<CreditNotePdfTabProps> = ({ note }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    pdfUrl,
    loadingPdf,
    errorPdf: errorLoadingPdf,
    loadingEmail,
  } = useAppSelector((state) => state.creditNote);

  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (note?.id && !pdfUrl && !loadingPdf) {
      dispatch(getCreditNotePdf(note.id));
    }
  }, [dispatch, note?.id, pdfUrl, loadingPdf]);

  useEffect(() => {
    return () => {
      dispatch(creditNoteActions.clearPdfUrl());
    };
  }, [dispatch]);

  const handleDownload = (): void => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `CreditNote_${note.noteNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = (): void => {
    if (!pdfUrl) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      setTimeout(() => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
        setTimeout(() => document.body.removeChild(iframe), 2000);
      }, 100);
    };
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Link copied to clipboard!");
    });
    // handleMenuClose();
  };

  const handleEmailClick = (): void => {
    setEmailModalOpen(true);
  };

  const handleEmailSubmit = async (email: string): Promise<void> => {
    const success = await dispatch(emailCreditNote(note.id, { email }));

    if (success) {
      setEmailModalOpen(false);
    }
  };

  // --- RENDER FUNCTIONS FOR ACTIONS ---
  const renderDesktopActions = (): React.JSX.Element => (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ mb: 3, justifyContent: "flex-end" }}
    >
      <AppButton
        variant="outlined"
        color="primary"
        startIcon={<ShareIcon />}
        onClick={handleShare}
      >
        Share Link
      </AppButton>
      <AppButton
        variant="outlined"
        color="secondary"
        startIcon={<EmailIcon />}
        onClick={handleEmailClick}
      >
        Email
      </AppButton>
      <AppButton
        variant="contained"
        color="warning"
        startIcon={<FileDownloadIcon />}
        onClick={handleDownload}
        disabled={!pdfUrl || loadingPdf}
      >
        Download
      </AppButton>
      <AppButton
        variant="contained"
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        disabled={!pdfUrl || loadingPdf}
      >
        Print Note
      </AppButton>
    </Stack>
  );

  const renderMobileActions = (): React.JSX.Element => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        px: 1,
      }}
    >
      <Typography
        variant="subtitle2"
        color="text.primary"
        sx={{ fontWeight: "bold" }}
      >
        Document Actions
      </Typography>
      <Stack direction="row" spacing={0.5}>
        <IconButton
          color="primary"
          onClick={handlePrint}
          disabled={!pdfUrl || loadingPdf}
        >
          <PrintIcon />
        </IconButton>
        <IconButton color="primary" onClick={handleShare}>
          <ShareIcon />
        </IconButton>
        <IconButton
          color="primary"
          onClick={handleDownload}
          disabled={!pdfUrl || loadingPdf}
        >
          <FileDownloadIcon />
        </IconButton>
        <IconButton color="primary" onClick={handleEmailClick}>
          <EmailIcon />
        </IconButton>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Conditionally render actions based on screen size */}
      {isMobile ? renderMobileActions() : renderDesktopActions()}

      {/* PDF Viewer */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: "650px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#1b1b1b" : "#e0e0e0",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {loadingPdf ? (
          <AppCircularProgress />
        ) : errorLoadingPdf ? (
          <>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography color="error">{errorLoadingPdf}</Typography>
            <AppButton
              pill
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => dispatch(getCreditNotePdf(note.id))}
            >
              Retry
            </AppButton>
          </>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title={`Credit Note PDF ${note?.noteNumber}`}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : null}
      </Paper>

      {/* Email Modal */}
      <AppEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        loading={loadingEmail}
        title="Email Document"
        description={`Send a copy of document #${note.noteNumber} directly to an email.`}
      />
    </Box>
  );
};

export default CreditNotePdfTab;
